import { unsafelyKeys } from "common/utilities/object";
import type { ApplyEventsSearchCriteriaAction, ClearEventsSearchCriteriaAction } from "domains/events/actionTypes";
import type { TopicEventSearchCriteria } from "domains/events/types";
import type {
  ApplyInitiativesSearchCriteriaAction,
  ClearInitiativesSearchCriteriaAction,
} from "domains/initiatives/actionTypes";
import type { TopicInitiativeState } from "domains/initiatives/types";
import type {
  TopicKnowledgeDataApplySearchCriteriaAction,
  TopicKnowledgeDataClearSearchCriteriaAction,
} from "domains/knowledgeData/actionTypes";
import type { TopicKnowledgeDataSearchCriteria } from "domains/knowledgeData/types";
import type { TopicLatestApplySearchCriteria, TopicLatestClearSearchCriteria } from "domains/knowledgeFeed/actionTypes";
import type {
  TopicPublicationApplySearchCriteria,
  TopicPublicationClearSearchCriteria,
} from "domains/publication/actionTypes";
import type { TopicPublicationSearchCriteria } from "domains/publication/types";
import type {
  TopicStakeholdersApplySearchCriteriaAction,
  TopicStakeholdersClearSearchCriteriaAction,
} from "domains/stakeholders/actionTypes";
import type { TopicStakeholdersSearchCriteria } from "domains/stakeholders/types";
import type {
  TopicVideoApplySearchCriteriaAction,
  TopicVideoClearSearchCriteriaAction,
} from "domains/videos/actionTypes";
import type { TopicVideosSearchCriteria } from "domains/videos/types";

type State =
  | TopicEventSearchCriteria
  | TopicInitiativeState["searchCriteria"]
  | TopicKnowledgeDataSearchCriteria
  | TopicPublicationSearchCriteria
  | TopicStakeholdersSearchCriteria
  | TopicVideosSearchCriteria;
type Action =
  | ApplyEventsSearchCriteriaAction
  | ApplyInitiativesSearchCriteriaAction
  | ClearEventsSearchCriteriaAction
  | ClearInitiativesSearchCriteriaAction
  | TopicKnowledgeDataApplySearchCriteriaAction
  | TopicKnowledgeDataClearSearchCriteriaAction
  | TopicLatestApplySearchCriteria
  | TopicLatestClearSearchCriteria
  | TopicPublicationApplySearchCriteria
  | TopicPublicationClearSearchCriteria
  | TopicStakeholdersApplySearchCriteriaAction
  | TopicStakeholdersClearSearchCriteriaAction
  | TopicVideoApplySearchCriteriaAction
  | TopicVideoClearSearchCriteriaAction;

export const searchCriteriaReducer =
  <S extends State>(initialState: S, APPLY_FILTER_TYPE: Action["type"], CLEAR_FILTER_TYPE: Action["type"]) =>
  (state = initialState, action: Action): S => {
    switch (action.type) {
      case APPLY_FILTER_TYPE: {
        if (!("payload" in action) || !action.payload) {
          return state;
        }
        const { offset, limit, topicId: _, ...filters } = action.payload;
        let newOffset = offset ?? state.offset;
        const newLimit = limit ?? state.limit;
        if (offset === undefined) {
          const searchCriteriaHasChanged = unsafelyKeys(filters).some((key) => filters[key] !== state[key]);
          if (searchCriteriaHasChanged) {
            newOffset = initialState.offset;
          }
        }

        return { ...state, ...filters, offset: newOffset, limit: newLimit };
      }

      case CLEAR_FILTER_TYPE: {
        const { search: _search, ...other } = state;

        return unsafelyKeys(other).reduce((newState, key) => ({ ...newState, [key]: initialState[key] }), state);
      }

      default:
        return state;
    }
  };
